/**
 * Smart, mostly-static chat timestamp formatter (no per-second ticking).
 *
 * Renders an RFC-3339 UTC instant in the user's **local** timezone, choosing a
 * format by how far in the past it is relative to `now`:
 *   - `< 60s`           → `Just now`
 *   - earlier today     → `10:32 am`            (clock only)
 *   - yesterday         → `Yesterday 4:15 pm`
 *   - older, this year  → `14 Jun, 9:02 am`      (no year)
 *   - prior years       → `14 Jun 2025, 9:02 am`
 *
 * `now` (epoch ms) is passed IN, not read from the clock, so the function is
 * pure + unit-testable and a single coarse refresh tick in the caller can
 * re-render every bubble at once ("Just now" → clock) without per-message
 * timers. Today/Yesterday are compared on **local** calendar fields so the day
 * boundary matches what the user sees, not UTC.
 *
 * Lives app-local (not in the design system): DS `MessageBubble.time` is a
 * preformatted display string by design — the DS never parses/formats dates.
 */
export function formatMessageTime(iso: string, now: number): string {
  const date = new Date(iso);
  const elapsedMs = now - date.getTime();

  // < 60s (and any small clock-skew / near-future drift) reads as "Just now".
  if (elapsedMs < 60_000) return 'Just now';

  // Locale clock, e.g. "10:32 am" — `numeric` hour (no leading zero); lowercase
  // the am/pm token to match the design. A 24h locale simply renders "10:32".
  const clock = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();

  const nowDate = new Date(now);
  const startOfLocalDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfLocalDay(nowDate) - startOfLocalDay(date)) / 86_400_000);

  if (dayDiff <= 0) return clock; // earlier today
  if (dayDiff === 1) return `Yesterday ${clock}`;

  const sameYear = date.getFullYear() === nowDate.getFullYear();
  const datePart = date.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${datePart}, ${clock}`;
}
